// 입력칸이 지금 무엇을 제안해야 하는가. **순수 함수다** — 커서 자리와 명단만 보고
// 정한다. 화면이 이 판단을 품으면 시험이 키보드를 흉내 내야 한다.

import { hangul_at } from './hangul.ts'
import { msg } from './i18n.ts'
import type { Candidate, MentionQuery } from './mention_menu.tsx'
import { emoji_query, mention_query, slash_of, slash_query } from './mention_menu.tsx'

export interface Suggestion {
  sigil: '@' | '/' | ':'
  query: MentionQuery
  items: Candidate[]
}

const MENU_MAX = 6

/**
 * 사람도 커맨드도 아닌 `@channel`·`@here`. 맨 뒤에 둔다 — 실수로 고르면 시끄럽다.
 * 귀띔은 원문이고 메뉴(`MentionMenu`)가 번역한다. 알림이 누구에게 가는지를 말한다.
 */
const ALL: readonly Candidate[] = [
  { id: 'channel', name: 'channel', hint: msg('채널의 모든 사람에게 알림') },
  { id: 'here', name: 'here', hint: msg('지금 접속한 사람에게 알림') },
]

/** 앞에서 맞는 것 — 치는 중의 한글(`ㅇ`·`하`)과 초성(`ㅇㅎㄴ`)도(`hangul.ts`). */
function starts(items: readonly Candidate[], prefix: string): Candidate[] {
  return items.filter((c) => hangul_at(c.name, prefix) === 0)
}

/** 앞글자로 거른다. 빈 앞글자면 앞 몇 개. */
export function candidates(people: readonly Candidate[], prefix: string): Candidate[] {
  return [...starts(people, prefix), ...starts(ALL, prefix)].slice(0, MENU_MAX)
}

/**
 * 커맨드가 사람보다 먼저다 — `/` 는 글 맨 앞에서만 뜨고, 그 자리에 `@` 는 올 수 없다.
 * 어느 쪽도 아니면 `null` 이고 메뉴는 없다.
 */
export function suggest(
  text: string,
  caret: number,
  people?: readonly Candidate[],
  commands?: readonly Candidate[],
  emoji?: readonly Candidate[],
): Suggestion | null {
  if (emoji !== undefined) {
    const q = emoji_query(text, caret)
    if (q !== null) {
      // 앞에서 맞는 것 먼저, 그다음 가운데 맞는 것(`:up` → `thumbsup`). 이름이 같은 별칭은 하나만.
      const head = emoji.filter((c) => c.name.startsWith(q.prefix))
      const mid = emoji.filter((c) => !c.name.startsWith(q.prefix) && c.name.includes(q.prefix))
      const items = [...head, ...mid].slice(0, MENU_MAX)
      return items.length === 0 ? null : { sigil: ':', query: q, items }
    }
  }
  if (commands !== undefined) {
    const q = slash_query(text, caret)
    if (q !== null) {
      const items = starts(commands, q.prefix).slice(0, MENU_MAX)
      return items.length === 0 ? null : { sigil: '/', query: q, items }
    }
  }
  if (people === undefined) return null
  const q = mention_query(text, caret)
  if (q === null) return null
  const items = candidates(people, q.prefix)
  return items.length === 0 ? null : { sigil: '@', query: q, items }
}

/**
 * 보낼 글이 아는 커맨드인가. 명단이 아직 없으면 `load` 로 받아 본다 — 명단을 처음 쓸 때 받으므로
 * `/remind …` 를 친 뒤 바로 Enter 를 누르면 여기서 기다린다. 못 받으면 그냥 글이다.
 */
export async function command_of(
  written: string,
  commands: readonly Candidate[] | undefined,
  load?: () => Promise<readonly Candidate[]>,
): Promise<{ name: string; text: string } | null> {
  const hit = slash_of(written)
  if (hit === null) return null
  const list = commands ?? (await load?.().catch(() => undefined))
  if (list === undefined) return null
  return list.some((c) => c.name === hit.name) ? hit : null
}
